import { CSSProperties, useState } from 'react';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import { useTemplateSecrets } from '@backstage/plugin-scaffolder-react';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';

type Entry = { key: string; secretRef: string };

const ConjurSecretListFieldSchema = makeFieldSchema({
  output: z => z.array(z.object({ key: z.string(), secretRef: z.string() })),
});

export const ConjurSecretListFieldSchemaSpec = ConjurSecretListFieldSchema;

type Props = typeof ConjurSecretListFieldSchema.TProps;

const MAX_ENTRIES = 10;

export const ConjurSecretListField = ({ onChange, rawErrors }: Props) => {
  const { secrets, setSecrets } = useTemplateSecrets();

  const [entries, setEntries] = useState<Entry[]>([
    { key: '', secretRef: 'conjur_0' },
  ]);
  const [nextIdx, setNextIdx] = useState(1);
  // Local state for value display so the TextField is controlled and fast.
  const [localValues, setLocalValues] = useState<Record<string, string>>({});
  const [visibleValues, setVisibleValues] = useState<Record<string, boolean>>(
    {},
  );

  const notifyChange = (updated: Entry[]) => {
    onChange(updated.filter(e => e.key.trim() !== ''));
  };

  const handleKeyChange = (i: number, key: string) => {
    const updated = entries.map((e, idx) => (idx === i ? { ...e, key } : e));
    setEntries(updated);
    notifyChange(updated);
  };

  const handleValueChange = (secretRef: string, value: string) => {
    setLocalValues(prev => ({ ...prev, [secretRef]: value }));
    setSecrets({ [secretRef]: value });
  };

  const toggleValueVisibility = (secretRef: string) => {
    setVisibleValues(prev => ({
      ...prev,
      [secretRef]: !prev[secretRef],
    }));
  };

  const addRow = () => {
    if (entries.length >= MAX_ENTRIES) return;
    const secretRef = `conjur_${nextIdx}`;
    const updated = [...entries, { key: '', secretRef }];
    setEntries(updated);
    setNextIdx(n => n + 1);
    notifyChange(updated);
  };

  const removeRow = (i: number) => {
    const { secretRef } = entries[i];
    setLocalValues(prev => {
      const { [secretRef]: _removed, ...rest } = prev;
      return rest;
    });
    setVisibleValues(prev => {
      const { [secretRef]: _removed, ...rest } = prev;
      return rest;
    });
    const updated = entries.filter((_, idx) => idx !== i);
    setEntries(updated);
    notifyChange(updated);
  };

  const hasError = (rawErrors?.length ?? 0) > 0;

  return (
    <Box>
      {hasError && (
        <Typography variant="caption" color="error">
          {rawErrors?.[0]}
        </Typography>
      )}
      {entries.map((entry, i) => (
        <Grid container spacing={1} alignItems="center" key={entry.secretRef}>
          <Grid item xs={6}>
            <TextField
              label="Key"
              placeholder="Conjur variable name"
              value={entry.key}
              onChange={e => handleKeyChange(i, e.target.value)}
              fullWidth
              margin="dense"
              error={hasError && !entry.key.trim()}
            />
          </Grid>
          <Grid item xs={5}>
            <TextField
              label="Value"
              value={
                localValues[entry.secretRef] ?? secrets[entry.secretRef] ?? ''
              }
              onChange={e => handleValueChange(entry.secretRef, e.target.value)}
              // Multiline renders a <textarea>, which ignores type="password",
              // so mask the value via -webkit-text-security instead.
              multiline
              minRows={1}
              maxRows={8}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        visibleValues[entry.secretRef]
                          ? 'Hide secret value'
                          : 'Show secret value'
                      }
                      edge="end"
                      onClick={() => toggleValueVisibility(entry.secretRef)}
                      onMouseDown={event => event.preventDefault()}
                    >
                      {visibleValues[entry.secretRef] ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              inputProps={{
                autoComplete: 'new-password',
                spellCheck: false,
                style: {
                  WebkitTextSecurity: visibleValues[entry.secretRef]
                    ? 'none'
                    : 'disc',
                } as CSSProperties,
              }}
              fullWidth
              margin="dense"
            />
          </Grid>
          <Grid item xs={1}>
            {entries.length > 1 && (
              <IconButton
                size="small"
                onClick={() => removeRow(i)}
                aria-label="Remove row"
              >
                <RemoveCircleOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Grid>
        </Grid>
      ))}
      {entries.length < MAX_ENTRIES && (
        <Box mt={1}>
          <IconButton size="small" onClick={addRow} aria-label="Add secret">
            <AddCircleOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};
